import type { BffPhysicalDeviceResourceReadApi } from '@ingenico/api-sdk-provisioning-private';
import type { ISectionContentCopyLabelCard } from '@ingenico/react-ui';
import { toString } from 'lodash-es';
import type { DeviceMetrics } from '../../domain/models/deviceMetrics.model';

export function getConfiguration<T, R extends object = object>(
  deviceConfigurationsDetails: T | null | undefined,
  key: keyof R | keyof T,
  label: string,
  isValueCopy?: boolean,
  physicalDevice?: R,
): ISectionContentCopyLabelCard[] {
  const value = physicalDevice?.[key as keyof R] ?? deviceConfigurationsDetails?.[key as keyof T];
  return value
    ? [
        {
          label,
          isValueCopy,
          value: label.includes('mode')
            ? toString(`logicalDevice:deviceMode.${toString(value).toLowerCase()}`) //@Pedro: Share this to Mohamed Sendi, Maxime and Matheus
            : toString(value),
          valueTooltip: true,
        },
      ]
    : [];
}

export const deviceConfigurationDisplayedInfo: Partial<
  Record<
    keyof BffPhysicalDeviceResourceReadApi | keyof DeviceMetrics | 'virtualReference',
    { readonly label: string; readonly isCopy?: boolean }
  >
> = {
  androidVersion: { label: 'logicalDevice:deviceProperties.androidVersion' },
  mode: { label: 'logicalDevice:deviceProperties.mode' },
  ICCD: { label: 'logicalDevice:deviceProperties.simICCID' },
  partNumber: { label: 'logicalDevice:deviceProperties.partNumber' },
  manufacturerPartNumber: { label: 'logicalDevice:deviceProperties.manufacturerPartNumber' },
  serialNumber: { label: 'logicalDevice:deviceProperties.serialNumber', isCopy: true },
  reference: { label: 'logicalDevice:deviceProperties.physicalReference', isCopy: true },
  id: { label: 'logicalDevice:deviceProperties.physicalId', isCopy: true },
  virtualReference: { label: 'logicalDevice:logicalDevice.virtualReference', isCopy: true },
};

export function getDisplayedConfigurationDetails(
  deviceConfigurationsDetails?: DeviceMetrics | null,
  physicalDevice?: BffPhysicalDeviceResourceReadApi,
  logicalDeviceReference?: string,
  shouldDisplayCustomerLayout?: boolean,
  shouldDisplayMerchantLayout?: boolean,
): ISectionContentCopyLabelCard[] {
  if (shouldDisplayCustomerLayout) {
    return getTetraConfigurationDetails(deviceConfigurationsDetails, physicalDevice);
  }

  if (shouldDisplayMerchantLayout) {
    return getMerchantTetraConfigurationDetails(deviceConfigurationsDetails, physicalDevice, logicalDeviceReference);
  }

  return getNormalConfigurationDetails(deviceConfigurationsDetails, physicalDevice, logicalDeviceReference);
}

function getTetraConfigurationDetails(
  deviceConfigurationsDetails?: DeviceMetrics | null,
  physicalDevice?: BffPhysicalDeviceResourceReadApi,
): ISectionContentCopyLabelCard[] {
  // For Tetra, display only partNumber, serialNumber, reference and id
  const tetraKeys = ['partNumber', 'serialNumber', 'reference', 'id'];

  const tetraDetails = tetraKeys.reduce(
    (accum, current) => [
      ...accum,
      ...getConfiguration<DeviceMetrics, BffPhysicalDeviceResourceReadApi>(
        deviceConfigurationsDetails,
        current as keyof DeviceMetrics,
        toString(deviceConfigurationDisplayedInfo[current as keyof typeof deviceConfigurationDisplayedInfo]?.label),
        deviceConfigurationDisplayedInfo[current as keyof typeof deviceConfigurationDisplayedInfo]?.isCopy,
        physicalDevice,
      ),
    ],
    [] as ISectionContentCopyLabelCard[],
  );

  return tetraDetails;
}

function getNormalConfigurationDetails(
  deviceConfigurationsDetails?: DeviceMetrics | null,
  physicalDevice?: BffPhysicalDeviceResourceReadApi,
  logicalDeviceReference?: string,
): ISectionContentCopyLabelCard[] {
  const baseDetails = Object.keys(deviceConfigurationDisplayedInfo).reduce(
    (accum, current) => [
      ...accum,
      ...getConfiguration<DeviceMetrics, BffPhysicalDeviceResourceReadApi>(
        deviceConfigurationsDetails,
        current as keyof DeviceMetrics,
        toString(deviceConfigurationDisplayedInfo[current as keyof typeof deviceConfigurationDisplayedInfo]?.label),
        deviceConfigurationDisplayedInfo[current as keyof typeof deviceConfigurationDisplayedInfo]?.isCopy,
        physicalDevice,
      ),
    ],
    [] as ISectionContentCopyLabelCard[],
  );

  const virtualReferenceDetail = logicalDeviceReference
    ? [
        {
          label: toString(deviceConfigurationDisplayedInfo.virtualReference?.label),
          isValueCopy: deviceConfigurationDisplayedInfo.virtualReference?.isCopy,
          value: logicalDeviceReference,
        },
      ]
    : [];

  return [...baseDetails, ...virtualReferenceDetail];
}

const deviceConfigurationDisplayedInfoWithIMEI = {
  ...deviceConfigurationDisplayedInfo,
  IMEI: { label: 'logicalDevice:deviceProperties.simIMEI', isCopy: false },
};

function getMerchantTetraConfigurationDetails(
  deviceConfigurationsDetails?: DeviceMetrics | null,
  physicalDevice?: BffPhysicalDeviceResourceReadApi,
  logicalDeviceReference?: string,
): ISectionContentCopyLabelCard[] {
  // For Merchant Tetra, display: Serial number (copy button), Android version, SIM ICCID, IMEI, Part number, Manufacturer part number, Virtual reference (copy button), Hardware reference (copy button)
  const merchantTetraKeys = ['serialNumber', 'partNumber', 'id'];

  const merchantTetraDetails = merchantTetraKeys.reduce(
    (accum, current) => [
      ...accum,
      ...getConfiguration<DeviceMetrics, BffPhysicalDeviceResourceReadApi>(
        deviceConfigurationsDetails,
        current as keyof DeviceMetrics,
        toString(
          deviceConfigurationDisplayedInfoWithIMEI[current as keyof typeof deviceConfigurationDisplayedInfoWithIMEI]
            ?.label,
        ),
        deviceConfigurationDisplayedInfoWithIMEI[current as keyof typeof deviceConfigurationDisplayedInfoWithIMEI]
          ?.isCopy,
        physicalDevice,
      ),
    ],
    [] as ISectionContentCopyLabelCard[],
  );

  // Add Virtual reference (copy button)
  const virtualReferenceDetail = logicalDeviceReference
    ? [
        {
          label: toString(deviceConfigurationDisplayedInfo.virtualReference?.label),
          isValueCopy: deviceConfigurationDisplayedInfo.virtualReference?.isCopy,
          value: logicalDeviceReference,
        },
      ]
    : [];

  // Add Hardware reference (copy button)
  const hardwareReferenceDetail = physicalDevice?.reference
    ? [
        {
          label: 'logicalDevice:deviceProperties.physicalReference',
          isValueCopy: true,
          value: toString(physicalDevice.reference),
        },
      ]
    : [];

  return [...merchantTetraDetails, ...virtualReferenceDetail, ...hardwareReferenceDetail];
}
